import { MRT_ColumnDef, useMaterialReactTable, MaterialReactTable } from 'material-react-table'
import React, { useEffect, useMemo, useState } from 'react'
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR'
import { DadosBorderoLanctoCartao, bgLineGridColorDark, bgLineGridColorLight, corBgGrid } from '../../Utils/Types'
import { useSelector } from 'react-redux'
import { selectUser } from '../Redux/userSlice'
import { ThemeProvider, createTheme } from '@mui/material'
import { Tooltip } from 'react-tooltip'
import { Funcoes } from '../../Utils/Funcoes'
import moment from 'moment'

type Props = {
    lanctoCartaoEstorno: DadosBorderoLanctoCartao[],
    handleSelectItemClick: (id: number, selected: boolean) => void
}

type VisibilityState = Record<string, boolean>

interface RowData {
    valor: number,
    valor_receber: number,
    id_bordero: number,
    dt_baixa: string,
    nome_cliente: string,
    obs_baixa: string
    qtd: number
    selected: boolean
}

export const GridLanctoCartaoEstorno = React.memo(({ lanctoCartaoEstorno, handleSelectItemClick }: Props) => {

    const defaultJson = useMemo(() => {
        return (

            localStorage.getItem("@commerce/column_grid_lanctoCartao_estorno")
                ? JSON.parse(localStorage.getItem("@commerce/column_grid_lanctoCartao_estorno") || '')
                : {
                    estornar: true,
                    valor: true,
                    valor_receber: true,
                    id_bordero: true,
                    dt_baixa: true,
                    nome_cliente: true,
                    obs_baixa: true,
                    qtd: true,
                    selected: true,
                }
        )
    }, [])

    const [state, setState] = useState(false)
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(defaultJson)

    const [lanctoCartoesEstorno, setLanctoCartoesEstorno] = useState<DadosBorderoLanctoCartao[]>([])

    let user = useSelector(state => selectUser(state))

    //---------------------------------------------------------------------------------------------------------------------------------
    //------------------------------------------------------------ EFFECT -------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        setLanctoCartoesEstorno(lanctoCartaoEstorno)
    }, [lanctoCartaoEstorno])

    useEffect(() => {
        localStorage.setItem("@commerce/column_grid_lanctoCartao_estorno", JSON.stringify(columnVisibility))
    }, [columnVisibility])

    //---------------------------------------------------------------------------------------------------------------------------------
    //------------------------------------------------------------ TEMAS --------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const theme = createTheme({
        palette: {
            mode: user.modeDark,
            background: {
                default:
                    user.modeDark === 'light' ? '#fff' : '#475569',
            },
        },
    })

    const baseBackgroundColor = user.modeDark === 'dark' ? '#475569' : '#fff'

    //---------------------------------------------------------------------------------------------------------------------------------
    //------------------------------------------------------------ COLUNAS ------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const handleCheckBoxClick = (id: number, selected: boolean) => {
        setState(!state)
        handleSelectItemClick(id, selected)
    }

    const colunas = useMemo<MRT_ColumnDef<DadosBorderoLanctoCartao>[]>(() => [

        {
            accessorKey: 'estornar',
            header: '[Estornar]',
            size: 50,
            visibleInShowHideMenu: true,
            muiTableBodyCellProps: { align: 'center' },
            muiTableHeadCellProps: { align: 'center' },
            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (

                    <div className={`w-full h-[30px]`} >

                        <input
                            id={'checkbox-' + rowData.id_bordero}
                            aria-label={rowData.id_bordero.toString()}
                            type='checkbox'
                            onChange={() => handleCheckBoxClick(rowData.id_bordero, rowData.selected)}
                            checked={!!rowData.selected}
                            className={`h-5 w-5 mx-2 cursor-pointer`}
                        />

                        <Tooltip
                            style={{ backgroundColor: 'rgb(30, 41, 59)', zIndex: 9999 }}
                            id={'tooltip-checkbox-' + rowData.id_bordero}
                            anchorSelect={'#checkbox-' + rowData.id_bordero}
                            positionStrategy='fixed' >
                            <span >
                                Selecione para <br />
                                estornar
                            </span>
                        </Tooltip>

                    </div>
                )
            }
        },

        {
            accessorKey: 'nome_cliente', visibleInShowHideMenu: true, header: 'Cliente', size: 250, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.nome_cliente ? rowData.nome_cliente : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'obs_baixa', visibleInShowHideMenu: true, header: 'Observação', size: 250, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.obs_baixa ? rowData.obs_baixa : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'dt_baixa', visibleInShowHideMenu: true, header: 'Data da Baixa', size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.dt_baixa ? moment(rowData.dt_baixa).format("DD/MM/YYYY") : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'valor', visibleInShowHideMenu: true, header: 'Valor', size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.valor ? Funcoes.numberFormat(rowData.valor, 'currency') : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'valor_receber', visibleInShowHideMenu: true, header: 'Valor a Receber', size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.valor_receber ? Funcoes.numberFormat(rowData.valor_receber, 'currency') : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'qtd', visibleInShowHideMenu: true, header: 'Qtde de duplicatas', size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.qtd ? rowData.qtd : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

        {
            accessorKey: 'id_bordero', visibleInShowHideMenu: true, header: 'Bordero', size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.id_bordero ? rowData.id_bordero : <span className={`text-red-400`}>N/D</span>}</span>)
            }
        },

    ], [lanctoCartaoEstorno])


    //---------------------------------------------------------------------------------------------------------------------------------
    //------------------------------------------------------------ TABLE --------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const table = useMaterialReactTable({
        paginationDisplayMode: 'pages',
        columns: colunas,
        data: lanctoCartoesEstorno,
        autoResetPageIndex: false,
        mrtTheme: (theme) => ({
            baseBackgroundColor: baseBackgroundColor,
        }),
        muiTableHeadCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`,
                backgroundColor: corBgGrid,
                color: `#fff`,
                '& .MuiTableSortLabel-root': {
                    color: '#fff !important',
                    '&:hover': {
                        color: '#fff !important',
                    },
                    '&.Mui-active': {
                        color: '#fff !important',
                    },
                    '& .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                    '&.Mui-active .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                },
                '& .MuiIconButton-root': {
                    color: '#fff',
                    '&:hover': {
                        color: '#fff',
                    },
                },

            },
        },
        muiTableBodyCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`
            }
        },
        localization: MRT_Localization_PT_BR,
        enableEditing: false,
        enableTopToolbar: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableFilters: false,
        enableMultiSort: false,
        enableSorting: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        onColumnVisibilityChange: setColumnVisibility,
        state: { columnVisibility },
    })

    return (
        <>
            <ThemeProvider theme={theme}>

                <MaterialReactTable table={table} />

            </ThemeProvider>
        </>
    )
})